import React, { memo, useState } from 'react';

import { areEqual } from 'Utils/equalityChecks';
import { EquipmentAsset } from 'Containers/SerializedEquipment/types';

import classes from './serializedEquipmentPool.module.css';

// ISO datetime -> Y-m-d for a <input type="date"> / compact display.
const toDateInput = (iso?: string | null): string => (iso ? iso.slice(0, 10) : '');

interface Props {
  assets: EquipmentAsset[];
  // When set, the matching asset's action buttons are disabled while its mutation is in flight.
  busyAssetId?: number | null;
  // When provided, deployed rows show their open placement's deploy date with an inline editor;
  // Save calls this with the new Y-m-d. Only supplied where an open placement is loaded (RocketDry).
  onEditDate?: (asset: EquipmentAsset, dateIn: string) => void;
  onCheckOut?: (asset: EquipmentAsset) => void;
  onHistory?: (asset: EquipmentAsset) => void;
  onDeploy?: (asset: EquipmentAsset) => void;
  onMove?: (asset: EquipmentAsset) => void;
  onEdit?: (asset: EquipmentAsset) => void;
  onRetire?: (asset: EquipmentAsset) => void;
}

function SerializedEquipmentPool({
  assets,
  busyAssetId,
  onEditDate,
  onCheckOut,
  onHistory,
  onDeploy,
  onMove,
  onEdit,
  onRetire,
}: Props) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');

  const startEdit = (asset: EquipmentAsset) => {
    setEditingId(asset.id);
    setEditValue(toDateInput(asset.current_placement?.date_in));
  };
  const saveEdit = (asset: EquipmentAsset) => {
    if (onEditDate) onEditDate(asset, editValue);
    setEditingId(null); // close optimistically; the container reloads and surfaces any error
  };

  if (assets.length === 0) {
    return (
      <p className={classes.empty} data-testid="pool-empty">
        No serialized equipment yet.
      </p>
    );
  }

  return (
    <ul className={classes.list} data-testid="serialized-equipment-pool">
      {assets.map((asset) => {
        const makeModel = [asset.manufacturer, asset.model].filter(Boolean).join(' ');
        const ids = [asset.serial_number, asset.asset_tag].filter(Boolean).join(' · ');
        const busy = busyAssetId === asset.id;
        return (
          <li key={asset.id} className={classes.row} data-testid="pool-row">
            <div className={classes.name}>
              <span className={classes.assetName}>{asset.name}</span>
              {makeModel && <span className={classes.meta}>{makeModel}</span>}
              {ids && <span className={classes.meta}>{ids}</span>}
              {onEditDate && asset.current_placement && (
                <span className={classes.meta} data-testid="deploy-date">
                  {editingId === asset.id ? (
                    <span className="d-flex align-items-center gap-1">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        data-testid="edit-date-input"
                      />
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        disabled={busy || !editValue}
                        onClick={() => saveEdit(asset)}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-muted"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <>
                      {`Deployed ${toDateInput(asset.current_placement.date_in)}`}
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 ms-1"
                        disabled={busy}
                        onClick={() => startEdit(asset)}
                      >
                        Edit date
                      </button>
                    </>
                  )}
                </span>
              )}
            </div>
            <div className={classes.right}>
              <span className={classes.status}>{asset.status}</span>
              {onHistory && (
                <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => onHistory(asset)}>
                  History
                </button>
              )}
              {onDeploy && asset.status === 'available' && (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  disabled={busy}
                  onClick={() => onDeploy(asset)}
                >
                  Deploy
                </button>
              )}
              {onMove && asset.status === 'deployed' && (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  disabled={busy}
                  onClick={() => onMove(asset)}
                >
                  Move
                </button>
              )}
              {onCheckOut && asset.status === 'deployed' && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={busy}
                  onClick={() => onCheckOut(asset)}
                >
                  {busy ? 'Checking out…' : 'Check out'}
                </button>
              )}
              {onEdit && asset.status !== 'retired' && (
                <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => onEdit(asset)}>
                  Edit
                </button>
              )}
              {onRetire && asset.status !== 'deployed' && asset.status !== 'retired' && (
                <button
                  type="button"
                  className="btn btn-link btn-sm text-danger"
                  disabled={busy}
                  onClick={() => onRetire(asset)}
                >
                  Retire
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

SerializedEquipmentPool.defaultProps = {
  busyAssetId: null,
  onEditDate: undefined,
  onCheckOut: undefined,
  onHistory: undefined,
  onDeploy: undefined,
  onMove: undefined,
  onEdit: undefined,
  onRetire: undefined,
};

const SerializedEquipmentPoolMemo = memo(SerializedEquipmentPool, areEqual);

export { SerializedEquipmentPoolMemo as SerializedEquipmentPool };
